import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calendar,
  BookOpen,
  Building2,
  Calculator,
  TrendingUp,
  Receipt,
  X,
  ChevronDown,
  ChevronRight,
  Settings,
  Users,
  Package,
  Wallet
} from 'lucide-react';
import { cn } from '../lib/utils';

const MOIS = [
  { slug: 'janvier', nom: 'Janvier', num: 1 },
  { slug: 'fevrier', nom: 'Février', num: 2 },
  { slug: 'mars', nom: 'Mars', num: 3 },
  { slug: 'avril', nom: 'Avril', num: 4 },
  { slug: 'mai', nom: 'Mai', num: 5 },
  { slug: 'juin', nom: 'Juin', num: 6 },
  { slug: 'juillet', nom: 'Juillet', num: 7 },
  { slug: 'aout', nom: 'Août', num: 8 },
  { slug: 'septembre', nom: 'Septembre', num: 9 },
  { slug: 'octobre', nom: 'Octobre', num: 10 },
  { slug: 'novembre', nom: 'Novembre', num: 11 },
  { slug: 'decembre', nom: 'Décembre', num: 12 }
];

function Sidebar({ isOpen, onClose }) {
  var location = useLocation();
  var [modulesOpen, setModulesOpen] = useState(true);
  
  var isModuleActive = location.pathname.includes('/fiscal') || 
                       location.pathname.includes('/rentabilite') || 
                       location.pathname.includes('/facturation') ||
                       location.pathname.includes('/catalogue') ||
                       location.pathname.includes('/crm') ||
                       location.pathname.includes('/tresorerie');
  
  var isMonthActive = location.pathname.includes('/mois/');
  var currentMonthSlug = isMonthActive ? location.pathname.split('/mois/')[1] : null;

  return (
    <aside 
      className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transform transition-transform duration-300 ease-in-out",
        "md:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-5 border-b border-slate-800">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-emerald-500 rounded-lg">
            <BookOpen className="h-5 w-5 text-white" />
          </div>
          <span className="font-bold text-lg tracking-tight">
            <span className="text-white">5min</span>
            <span className="text-emerald-400">Compta</span>
          </span>
        </div>
        <button 
          onClick={onClose}
          className="md:hidden p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
          data-testid="close-sidebar-btn"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-64px)]">
        
        {/* Dashboard */}
        <NavLink
          to="/app"
          end
          onClick={onClose}
          className={function(props) { 
            return cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
              "hover:bg-slate-800",
              props.isActive ? "bg-blue-600 text-white" : "text-slate-300"
            );
          }}
          data-testid="nav-dashboard"
        >
          <LayoutDashboard className="h-5 w-5" />
          <span className="font-medium">Dashboard</span>
        </NavLink>

        {/* Mon entreprise */}
        <NavLink
          to="/app/entreprise"
          onClick={onClose}
          className={function(props) { 
            return cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
              "hover:bg-slate-800",
              props.isActive ? "bg-blue-600 text-white" : "text-slate-300"
            );
          }}
          data-testid="nav-entreprise"
        >
          <Building2 className="h-5 w-5" />
          <span className="font-medium">Mon entreprise</span>
        </NavLink>

        {/* Modules Avancés - Collapsible */}
        <div>
          <button
            onClick={function() { setModulesOpen(!modulesOpen); }}
            className={cn(
              "w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200",
              "hover:bg-slate-800",
              isModuleActive ? "bg-slate-800 text-white" : "text-slate-300"
            )}
          >
            <div className="flex items-center gap-3">
              <Calculator className="h-5 w-5" />
              <span className="font-medium">Modules</span>
            </div>
            {modulesOpen ? (
              <ChevronDown className="h-4 w-4 text-slate-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-500" />
            )}
          </button>
          
          {modulesOpen && (
            <div className="mt-1 ml-3 pl-3 border-l border-slate-700 space-y-1">
              <NavLink
                to="/app/fiscal"
                onClick={onClose}
                className={function(props) { 
                  return cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm",
                    "hover:bg-slate-800",
                    props.isActive ? "bg-blue-600 text-white" : "text-slate-400"
                  );
                }}
                data-testid="nav-fiscal"
              >
                <Receipt className="h-4 w-4" />
                <span>Reporting Fiscal</span>
              </NavLink>
              
              <NavLink
                to="/app/rentabilite"
                onClick={onClose}
                className={function(props) { 
                  return cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm",
                    "hover:bg-slate-800",
                    props.isActive ? "bg-blue-600 text-white" : "text-slate-400"
                  );
                }}
                data-testid="nav-rentabilite"
              >
                <TrendingUp className="h-4 w-4" />
                <span>Rentabilité</span>
              </NavLink>
              
              <NavLink
                to="/app/facturation"
                onClick={onClose}
                className={function(props) { 
                  return cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm",
                    "hover:bg-slate-800",
                    props.isActive ? "bg-blue-600 text-white" : "text-slate-400"
                  );
                }}
                data-testid="nav-facturation"
              >
                <Receipt className="h-4 w-4" />
                <span>Facturation</span>
              </NavLink>
              
              <NavLink
                to="/app/catalogue"
                onClick={onClose}
                className={function(props) { 
                  return cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm",
                    "hover:bg-slate-800",
                    props.isActive ? "bg-blue-600 text-white" : "text-slate-400"
                  );
                }}
                data-testid="nav-catalogue"
              >
                <Package className="h-4 w-4" />
                <span>Catalogue</span>
              </NavLink>
              
              <NavLink
                to="/app/crm"
                onClick={onClose}
                className={function(props) { 
                  return cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm",
                    "hover:bg-slate-800",
                    props.isActive ? "bg-blue-600 text-white" : "text-slate-400"
                  );
                }}
                data-testid="nav-crm"
              >
                <Users className="h-4 w-4" />
                <span>CRM Clients</span>
              </NavLink>
              
              <NavLink
                to="/app/tresorerie"
                onClick={onClose}
                className={function(props) { 
                  return cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm",
                    "hover:bg-slate-800",
                    props.isActive ? "bg-blue-600 text-white" : "text-slate-400"
                  );
                }}
                data-testid="nav-tresorerie"
              >
                <Wallet className="h-4 w-4" />
                <span>Trésorerie</span>
              </NavLink>
            </div>
          )}
        </div>

        {/* Section Label - MOIS */}
        <p className="px-3 pt-4 pb-1 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
          Mois
        </p>

        {/* Mois - Listed Vertically (Always Visible) */}
        <div className="space-y-0.5">
          {MOIS.map(function(mois) {
            var isActive = currentMonthSlug === mois.slug;
            return (
              <NavLink
                key={mois.slug}
                to={'/app/mois/' + mois.slug}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm",
                  "hover:bg-slate-800",
                  isActive ? "bg-blue-600 text-white" : "text-slate-400"
                )}
                data-testid={'nav-' + mois.slug}
              >
                <Calendar className="h-4 w-4" />
                <span>{mois.nom}</span>
              </NavLink>
            );
          })}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom Section - Settings */}
        <div className="pt-4 mt-4 border-t border-slate-800">
          <NavLink
            to="/app/parametres"
            onClick={onClose}
            className={function(props) { 
              return cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                "hover:bg-slate-800",
                props.isActive ? "bg-blue-600 text-white" : "text-slate-400"
              );
            }}
            data-testid="nav-parametres"
          >
            <Settings className="h-5 w-5" />
            <span className="font-medium">Paramètres</span>
          </NavLink>
        </div>

      </nav>
    </aside>
  );
}

export default Sidebar;
